import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';

import useResourceMuteNotifications from 'app/components/Resources/hooks/useResourceMuteNotifications';
import Tooltip from 'app/components/Tooltip';
import { BellIcon, BellOffIcon } from 'app/components/shared/icons';
import { AnimatePresence } from 'framer-motion';
import useCommentsCounts from 'hooks/useCommentsCounts';
import { MessageableType } from 'services/messages';
import { singularOrPlural } from 'utils';

import { IComment } from '../Comment';
import CommentThread from '../CommentThread';
import SubmitComment from '../SubmitComment';
import {
  CommentsListCommentWrapper,
  CommentsListTitle,
  CommentsListTitleContainer,
  CommentsListTitleIconContainer,
} from './styled';

interface CommentsListProps {
  messages: IComment[];
  messageable_type: MessageableType;
  messageable_uuid_or_slug: string;
  notifications_status: boolean;
  onUpdateNotificationsStatus?: (status: boolean) => void;
  id?: string;
}

export default function CommentsList({
  messages,
  messageable_type,
  messageable_uuid_or_slug,
  notifications_status,
  onUpdateNotificationsStatus,
  id = null,
}: CommentsListProps) {
  const queryClient = useQueryClient();
  const { handleAddComment, handleRemoveComment } = useCommentsCounts({
    type: messageable_type,
    uuid: messageable_uuid_or_slug,
  });
  const { mutate: updateNotificationPreferences } = useResourceMuteNotifications();
  const [isBellOn, setIsBellOn] = useState<boolean>(notifications_status);
  const [comments, setComments] = useState<IComment[]>(() => {
    const deletedMessagesWithoutChildMessagesUuids = messages
      .filter(
        (message) =>
          message.deleted_at && message.child_messages.filter((m) => !m.deleted_at).length === 0
      )
      .map((m) => m.uuid);

    return messages.filter(
      (message) => !deletedMessagesWithoutChildMessagesUuids.includes(message.uuid)
    );
  });

  useEffect(() => {
    const action = new URLSearchParams(window.location.search).get('action');
    if (action && action === 'disable_notifications') {
      updateNotificationPreferences({
        notifiable_type: messageable_type,
        notifiable_uuid: messageable_uuid_or_slug,
      });
      setIsBellOn(false);

      const url = new URL(window.location.href);
      url.searchParams.delete('action');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const sortedMessages = useMemo<IComment[]>(() => {
    return comments.sort((a, b) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [comments]);

  const handleCommentFinish = (comment: IComment) => {
    handleAddComment();
    queryClient.invalidateQueries({
      queryKey: [`resource-${messageable_uuid_or_slug}-messages`],
      refetchActive: false,
      refetchInactive: false,
    });
    setComments([...comments, comment]);
  };

  const handleCommentDelete = (comment: IComment) => {
    handleRemoveComment();
    queryClient.invalidateQueries({
      queryKey: [`resource-${messageable_uuid_or_slug}-messages`],
      refetchActive: false,
      refetchInactive: false,
    });
    setComments(comments.filter((c) => c.uuid !== comment.uuid));
  };

  const handleCommentUpdate = (comment: IComment) => {
    queryClient.invalidateQueries({
      queryKey: [`resource-${messageable_uuid_or_slug}-messages`],
      refetchActive: false,
      refetchInactive: false,
    });
    setComments(comments.map((c) => (c.uuid === comment.uuid ? comment : c)));
  };

  const commentsCount = useMemo(() => {
    return comments?.reduce((acc, comment) => {
      const childMessages = comment?.child_messages?.filter((m) => !m.deleted_at);

      if (childMessages?.length > 0) {
        return acc + childMessages.length + (comment?.deleted_at ? 0 : 1);
      }

      return acc + 1;
    }, 0);
  }, [comments]);

  const commentsTitle = useMemo(() => {
    if (commentsCount === 0) {
      return 'Be the first to comment';
    }

    return `${commentsCount} ${singularOrPlural(commentsCount, 'comment', 'comments')}`;
  }, [commentsCount]);

  const bellTitle = useMemo(() => {
    if (isBellOn) {
      return 'You’ll receive an notification when someone likes or comments on this resource.';
    }

    return `Notifications have been  muted for you for this resource.`;
  }, [isBellOn]);

  const handleBellClick = () => {
    updateNotificationPreferences({
      notifiable_type: messageable_type,
      notifiable_uuid: messageable_uuid_or_slug,
    });
    onUpdateNotificationsStatus(!isBellOn);

    setIsBellOn(!isBellOn);
  };

  return (
    <>
      <CommentsListTitleContainer>
        <CommentsListTitle>{commentsTitle}</CommentsListTitle>
        <CommentsListTitleIconContainer onClick={handleBellClick}>
          <Tooltip text={bellTitle}>
            <div>
              {isBellOn ? (
                <BellIcon width={24} height={24} fill="#ffffff" />
              ) : (
                <BellOffIcon width={24} height={24} fill="#ffffff" />
              )}
            </div>
          </Tooltip>
        </CommentsListTitleIconContainer>
      </CommentsListTitleContainer>
      <SubmitComment
        placeholder={commentsCount === 0 ? 'Start the conversation...' : 'Join the conversation...'}
        messageable_type={messageable_type}
        messageable_uuid_or_slug={messageable_uuid_or_slug}
        onFinish={handleCommentFinish}
        id={id}
      />
      <AnimatePresence>
        {sortedMessages.map((thread) => (
          <CommentsListCommentWrapper key={thread.uuid}>
            <CommentThread
              message={thread}
              messageable_type={messageable_type}
              messageable_uuid_or_slug={messageable_uuid_or_slug}
              onDelete={handleCommentDelete}
              onUpdate={handleCommentUpdate}
            />
          </CommentsListCommentWrapper>
        ))}
      </AnimatePresence>
    </>
  );
}
